import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { fetchStoriesCatalog, resolveStoryAssetUrl } from '../../utils/storiesFetcher';
import { getCharacter } from '../../utils/characters';
import { hasBridge } from '../../utils/storyBridge';
import { trackStorySelected } from '../../utils/analytics';

const CHAR_SESSION_KEY = 'explore_stories_character';
const ITEM_SESSION_KEY = 'explore_stories_selected_item';

// ---------------------------------------------------------------------------
// Styles (match TopicsPage / StoriesHomePage visual language)
// ---------------------------------------------------------------------------

const pageStyle = {
  minHeight: '100vh',
  background: 'var(--color-bg)',
  padding: '28px 18px 48px',
  maxWidth: 760,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--color-primary)',
  textDecoration: 'none',
  marginBottom: 24,
};

const sectionLabelStyle = {
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--color-text)',
  opacity: 0.55,
  marginBottom: 14,
  marginTop: 0,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
  gap: 18,
};

// ---------------------------------------------------------------------------
// SkeletonCard
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div
      style={{
        borderRadius: 18,
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        boxShadow: '0 4px 18px var(--color-shadow)',
      }}
      aria-hidden
    >
      <div className="stories-skeleton" style={{ height: 160, background: 'var(--color-border)' }} />
      <div style={{ padding: '14px 16px 18px' }}>
        <div className="stories-skeleton" style={{ height: 18, borderRadius: 8, width: '70%', marginBottom: 8, background: 'var(--color-border)' }} />
        <div className="stories-skeleton" style={{ height: 12, borderRadius: 6, width: '90%', marginBottom: 5, background: 'var(--color-border)' }} />
        <div className="stories-skeleton" style={{ height: 12, borderRadius: 6, width: '60%', background: 'var(--color-border)' }} />
      </div>
    </div>
  );
}

function SkeletonHero() {
  return (
    <>
      <div className="stories-skeleton" style={{ height: 220, borderRadius: 18, background: 'var(--color-border)', marginBottom: 20 }} aria-hidden />
      <div style={{ marginBottom: 28 }}>
        <div className="stories-skeleton" style={{ height: 32, borderRadius: 10, width: '40%', marginBottom: 10, background: 'var(--color-border)' }} />
        <div className="stories-skeleton" style={{ height: 16, borderRadius: 6, width: '80%', background: 'var(--color-border)' }} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// StoryCard
// ---------------------------------------------------------------------------

function StoryCard({ item, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const bridged = hasBridge(item.id);

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        width: '100%',
        padding: 0,
        borderRadius: 18,
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        background: 'var(--color-surface, var(--color-bg-card, var(--color-bg)))',
        boxShadow: hovered
          ? '0 12px 32px var(--color-shadow)'
          : '0 4px 18px var(--color-shadow)',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        cursor: 'pointer',
        textAlign: 'left',
        position: 'relative',
      }}
      aria-label={item.title}
    >
      {/* Image + overlay badges */}
      <div style={{ position: 'relative' }}>
        {item.image ? (
          <img
            src={resolveStoryAssetUrl(item.image)}
            alt=""
            aria-hidden
            style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            aria-hidden
            style={{
              width: '100%',
              height: 160,
              background: 'var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 40,
            }}
          >
            📖
          </div>
        )}

        {/* Bridge dot — top-right corner, subtle coloured indicator */}
        {bridged && (
          <div
            title="This story has a paired lesson"
            aria-label="Has paired lesson"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: 'var(--color-primary)',
              border: '2px solid var(--color-bg)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }}
          />
        )}
      </div>

      {/* Text body */}
      <div style={{ padding: '14px 16px 18px' }}>
        <div
          style={{
            fontSize: 'clamp(1rem, 2.8vw, 1.1rem)',
            fontWeight: 700,
            color: 'var(--color-text)',
            marginBottom: 5,
            lineHeight: 1.25,
          }}
        >
          {item.title}
        </div>
        {item.desc ? (
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--color-text)',
              opacity: 0.65,
            }}
          >
            {item.desc}
          </div>
        ) : null}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// StoriesCharacterPage
// ---------------------------------------------------------------------------

export default function StoriesCharacterPage() {
  const { character: characterParam } = useParams();
  const navigate = useNavigate();

  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroHovered, setHeroHovered] = useState(false);

  const loadCharacter = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Try sessionStorage — set by StoriesHomePage on character tap.
      const raw = sessionStorage.getItem(CHAR_SESSION_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        if (stored?.title?.toLowerCase() === characterParam?.toLowerCase()) {
          setCharacter(stored);
          setLoading(false);
          return;
        }
      }

      // 2. Fallback: fetch catalog and find by URL param (deep-link / reload).
      const catalog = await fetchStoriesCatalog();
      const found = catalog.find(
        (c) => c.title?.toLowerCase() === characterParam?.toLowerCase(),
      );
      if (!found) {
        setError(`No stories found for "${characterParam}".`);
        return;
      }
      if (!getCharacter(found.title)) {
        setError('This character is not available.');
        return;
      }
      setCharacter(found);
    } catch (err) {
      console.error('[StoriesCharacterPage] load failed:', err);
      setError('Could not load stories. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [characterParam]);

  useEffect(() => {
    loadCharacter();
  }, [loadCharacter]);

  const handleStorySelect = useCallback(
    (item, storyIndex) => {
      trackStorySelected({ storyId: item.id, character: character?.title || characterParam, storyIndex });
      try {
        sessionStorage.setItem(ITEM_SESSION_KEY, JSON.stringify(item));
      } catch {
        // sessionStorage unavailable in some sandboxed contexts
      }
      navigate(`/stories/${characterParam}/${item.id}`);
    },
    [navigate, characterParam, character],
  );

  const characterItems = Array.isArray(character?.items) ? character.items : [];

  const handleHeroClick = useCallback(() => {
    const firstStory = characterItems[0];
    if (!firstStory) return;
    trackStorySelected({ storyId: firstStory.id, character: character?.title || characterParam, storyIndex: 0 });
    try {
      sessionStorage.setItem(ITEM_SESSION_KEY, JSON.stringify(firstStory));
    } catch {
      // sessionStorage unavailable in some sandboxed contexts
    }
    navigate(`/stories/${characterParam}/${firstStory.id}`);
  }, [characterItems, navigate, characterParam, character]);

  return (
    <>
      <style>{`
        @keyframes stories-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.38; }
        }
        .stories-skeleton {
          animation: stories-pulse 1.4s ease-in-out infinite;
        }
      `}</style>

      <div style={pageStyle}>
        {/* Back to Stories home */}
        <Link to="/stories" style={backLinkStyle} aria-label="Back to all characters">
          <ChevronLeft size={20} strokeWidth={2.4} aria-hidden />
          Stories
        </Link>

        {/* ── Loading ── */}
        {loading && (
          <>
            <SkeletonHero />
            <p style={sectionLabelStyle}>Stories</p>
            <div style={gridStyle} aria-busy="true" aria-label="Loading stories">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </>
        )}

        {/* ── Error ── */}
        {!loading && error && (
          <div role="alert" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <p style={{ fontSize: 16, color: 'var(--color-text)', opacity: 0.75, marginBottom: 20, lineHeight: 1.5 }}>
              {error}
            </p>
            <button
              type="button"
              onClick={loadCharacter}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: 'none',
                background: 'var(--color-primary)',
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 10px var(--color-shadow)',
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* ── Character + stories ── */}
        {!loading && !error && character && (
          <>
            {/* Hero image — tap to start first story */}
            {(character.image || character.teaser) && (
              <div
                role={characterItems.length > 0 ? 'button' : undefined}
                tabIndex={characterItems.length > 0 ? 0 : undefined}
                onClick={characterItems.length > 0 ? handleHeroClick : undefined}
                onKeyDown={
                  characterItems.length > 0
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleHeroClick();
                        }
                      }
                    : undefined
                }
                onMouseEnter={() => setHeroHovered(true)}
                onMouseLeave={() => setHeroHovered(false)}
                aria-label={
                  characterItems.length > 0
                    ? `Start with ${characterItems[0].title || 'story 1'}`
                    : character.title
                }
                style={{
                  position: 'relative',
                  marginBottom: 20,
                  borderRadius: 18,
                  overflow: 'hidden',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 4px 18px var(--color-shadow)',
                  cursor: characterItems.length > 0 ? 'pointer' : 'default',
                }}
              >
                <img
                  src={resolveStoryAssetUrl(character.image || character.teaser)}
                  alt={character.title}
                  style={{
                    width: '100%',
                    maxHeight: 260,
                    objectFit: 'cover',
                    display: 'block',
                    filter: heroHovered && characterItems.length > 0 ? 'brightness(1.05)' : 'none',
                    transition: 'filter 0.18s ease',
                  }}
                />
                {characterItems.length > 0 ? (
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      bottom: 12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(0,0,0,0.5)',
                      color: '#fff',
                      padding: '6px 16px',
                      borderRadius: 20,
                      fontSize: 13,
                      backdropFilter: 'blur(4px)',
                      WebkitBackdropFilter: 'blur(4px)',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                    }}
                  >
                    ▶ Start with story 1
                  </div>
                ) : null}
              </div>
            )}

            {/* Character name + description */}
            <div style={{ marginBottom: 28 }}>
              <h1
                style={{
                  fontSize: 'clamp(1.5rem, 4vw, 1.85rem)',
                  fontWeight: 800,
                  color: 'var(--color-text)',
                  margin: '0 0 8px',
                  letterSpacing: '-0.02em',
                }}
              >
                {character.title}
              </h1>
              {character.desc ? (
                <p style={{ fontSize: 16, lineHeight: 1.5, color: 'var(--color-text)', opacity: 0.65, margin: 0 }}>
                  {character.desc}
                </p>
              ) : null}
            </div>

            {/* Stories section */}
            {characterItems.length > 0 ? (
              <>
                <p style={sectionLabelStyle}>
                  {characterItems.length} {characterItems.length === 1 ? 'Story' : 'Stories'}
                </p>
                <div style={gridStyle}>
                  {characterItems.map((item, index) => (
                    <StoryCard
                      key={item.id || index}
                      item={item}
                      onSelect={(i) => handleStorySelect(i, index)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p style={{ fontSize: 16, color: 'var(--color-text)', opacity: 0.6, marginTop: 8 }}>
                No stories available yet.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}
